import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { urlPagamentoSegura } from '@/shared/utils/pagamentoUrl';

import type {
  CadastroForm,
  CnpjStatus,
  PagamentoStatus,
  PlanoApi,
  PlanoTier,
  SelfStep,
} from '../types';
import {
  carregarPlanosSelf,
  consultarCnpjSelf,
  consultarPagamentoSelf,
  enviarCadastroSelf,
  iniciarPagamentoSelf,
} from './actions';

interface NotificacaoSelfState {
  step: SelfStep;
  form: CadastroForm;
  medicaoAutorizada: boolean;
  cnpjStatus: CnpjStatus;
  cnpjErro: string | null;
  planos: PlanoApi[];
  carregandoPlanos: boolean;
  planosErro: string | null;
  enviandoCadastro: boolean;
  cadastroErro: string | null;
  leadId: string | null;
  accessToken: string | null;
  planoTier: PlanoTier;
  pagamentoStatus: PagamentoStatus;
  pagamentoErro: string | null;
  iniciandoPagamento: boolean;
  pagamentoIniciado: boolean;
  invoiceUrl: string | null;
  consultandoPagamento: boolean;
  pollingPagamentoExpirado: boolean;
  sessaoExpirada: boolean;
}

const formVazio: CadastroForm = {
  cnpj: '',
  razaoSocial: '',
  nomeFantasia: '',
  telefone: '',
  responsavelNome: '',
  responsavelEmail: '',
  termosAceitos: true,
};

const initialState: NotificacaoSelfState = {
  step: 'inicio',
  form: formVazio,
  medicaoAutorizada: false,
  cnpjStatus: 'idle',
  cnpjErro: null,
  planos: [],
  carregandoPlanos: false,
  planosErro: null,
  enviandoCadastro: false,
  cadastroErro: null,
  leadId: null,
  accessToken: null,
  planoTier: 'starter',
  pagamentoStatus: 'aguardando',
  pagamentoErro: null,
  iniciandoPagamento: false,
  pagamentoIniciado: false,
  invoiceUrl: null,
  consultandoPagamento: false,
  pollingPagamentoExpirado: false,
  sessaoExpirada: false,
};

const notificacaoSelfSlice = createSlice({
  name: 'notificacaoSelf',
  initialState,
  reducers: {
    setStep: (state, action: PayloadAction<SelfStep>) => {
      state.step = action.payload;
    },
    setCampo: (
      state,
      action: PayloadAction<{ campo: keyof Omit<CadastroForm, 'termosAceitos'>; valor: string }>,
    ) => {
      state.form[action.payload.campo] = action.payload.valor;
    },
    setTermosAceitos: (state, action: PayloadAction<boolean>) => {
      state.form.termosAceitos = action.payload;
    },
    setMedicaoAutorizada: (state, action: PayloadAction<boolean>) => {
      state.medicaoAutorizada = action.payload;
    },
    resetCnpjStatus: (state) => {
      state.cnpjStatus = 'idle';
      state.cnpjErro = null;
      state.form.razaoSocial = '';
    },
    setPlanoTier: (state, action: PayloadAction<PlanoTier>) => {
      state.planoTier = action.payload;
    },
    marcarPollingPagamentoExpirado: (state) => {
      state.pollingPagamentoExpirado = true;
    },
    expirarSessao: (state) => {
      state.sessaoExpirada = true;
      state.step = 'cadastro';
    },
    limparAvisoSessao: (state) => {
      state.sessaoExpirada = false;
    },
    reiniciar: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(carregarPlanosSelf.pending, (state) => {
        state.carregandoPlanos = true;
        state.planosErro = null;
      })
      .addCase(carregarPlanosSelf.fulfilled, (state, action) => {
        state.carregandoPlanos = false;
        state.planos = action.payload;
        const tierServido = action.payload.some((plano) => plano.tier === state.planoTier);
        if (!tierServido && action.payload.length > 0) {
          state.planoTier = action.payload[0].tier;
        }
      })
      .addCase(carregarPlanosSelf.rejected, (state, action) => {
        state.carregandoPlanos = false;
        state.planosErro = action.payload ?? 'Erro ao carregar os planos';
      })
      .addCase(consultarCnpjSelf.pending, (state) => {
        state.cnpjStatus = 'consultando';
        state.cnpjErro = null;
        state.form.razaoSocial = '';
      })
      .addCase(consultarCnpjSelf.fulfilled, (state, action) => {
        state.cnpjStatus = action.payload.status;
        if (action.payload.status === 'ok') {
          state.form.razaoSocial = action.payload.razaoSocial ?? '';
          if (action.payload.nomeFantasia) state.form.nomeFantasia = action.payload.nomeFantasia;
          if (action.payload.telefone) state.form.telefone = action.payload.telefone;
        }
      })
      .addCase(consultarCnpjSelf.rejected, (state, action) => {
        if (action.meta.aborted) return;
        state.cnpjStatus = 'invalido';
        state.cnpjErro = action.payload ?? 'Erro ao consultar o CNPJ';
      })
      .addCase(enviarCadastroSelf.pending, (state) => {
        state.enviandoCadastro = true;
        state.cadastroErro = null;
      })
      .addCase(enviarCadastroSelf.fulfilled, (state, action) => {
        state.enviandoCadastro = false;
        if (state.leadId !== action.payload.leadId) {
          state.pagamentoStatus = 'aguardando';
          state.pagamentoIniciado = false;
        }
        state.leadId = action.payload.leadId;
        state.accessToken = action.payload.accessToken;
        state.sessaoExpirada = false;
        if (action.meta.arg.irParaRevisao) state.step = 'revisao';
      })
      .addCase(enviarCadastroSelf.rejected, (state, action) => {
        state.enviandoCadastro = false;
        state.cadastroErro = action.payload ?? 'Erro ao enviar o cadastro';
      })
      .addCase(iniciarPagamentoSelf.pending, (state) => {
        state.iniciandoPagamento = true;
        state.pagamentoErro = null;
        state.pollingPagamentoExpirado = false;
      })
      .addCase(iniciarPagamentoSelf.fulfilled, (state, action) => {
        state.iniciandoPagamento = false;
        state.pagamentoIniciado = true;
        state.step = 'pagamento';
        if (action.payload.convertido) {
          state.invoiceUrl = action.payload.invoiceUrl;
          state.pagamentoStatus = 'sucesso';
          return;
        }
        if (!urlPagamentoSegura(action.payload.invoiceUrl)) {
          state.invoiceUrl = null;
          state.pagamentoStatus = 'erro';
          state.pagamentoErro =
            'Não recebemos um link de pagamento válido. Tente novamente ou fale com o suporte.';
          return;
        }
        state.invoiceUrl = action.payload.invoiceUrl;
        state.pagamentoStatus = 'aguardando';
      })
      .addCase(iniciarPagamentoSelf.rejected, (state, action) => {
        state.iniciandoPagamento = false;
        if (!state.leadId || !state.accessToken) {
          state.sessaoExpirada = true;
          state.step = 'cadastro';
          return;
        }
        state.pagamentoErro = action.payload ?? 'Erro ao iniciar o pagamento';
        if (state.step === 'pagamento') {
          state.pagamentoStatus = 'erro';
        }
      })
      .addCase(consultarPagamentoSelf.pending, (state) => {
        state.consultandoPagamento = true;
        state.pollingPagamentoExpirado = false;
      })
      .addCase(consultarPagamentoSelf.fulfilled, (state, action) => {
        state.consultandoPagamento = false;
        if (state.pagamentoStatus !== 'aguardando') return;
        if (action.payload.convertido) {
          state.pagamentoStatus = 'sucesso';
          return;
        }
        if (action.payload.status === 'CANCELADO') {
          state.pagamentoStatus = 'erro';
          state.pagamentoErro = 'O pagamento pode ter sido recusado ou cancelado.';
        }
      })
      .addCase(consultarPagamentoSelf.rejected, (state) => {
        state.consultandoPagamento = false;
      });
  },
});

export const {
  setStep,
  setCampo,
  setTermosAceitos,
  setMedicaoAutorizada,
  resetCnpjStatus,
  setPlanoTier,
  marcarPollingPagamentoExpirado,
  expirarSessao,
  limparAvisoSessao,
  reiniciar,
} = notificacaoSelfSlice.actions;

export default notificacaoSelfSlice.reducer;
